import { useContext, useState } from "react";
import { useHistory } from "react-router";
import {
  type Failure,
  type PassengersForFareRequest,
  type PassengersResponse,
  PassengersResponseEnum,
  type PersonListResponse,
  type PersonListSuccess,
  SeatMapResponseEnum,
  type TripPricingSummaryResponse,
  type TripPricingSummarySuccess,
} from "@b2bportal/air-booking-api";
import type { LocationResponse } from "@b2bportal/air-shopping-api";
import {
  type CipherText,
  Product,
  type QuoteBreakdownResponse,
  QuoteState,
} from "@b2bportal/purchase-api";
import {
  fetchLocationAutocomplete,
  fetchTripDetails,
  fetchTripPricing,
  fetchUserPassengers,
  getQuoteBreakdown,
  validateUserPassengers,
} from "@hopper-b2b/api";

import {
  type ResumeStep,
  ResumeCheckoutComponent,
  type SetContactInformationContext,
  ResumeEvent,
  parseFlightsQueryString,
  getFlightSelectedLocation,
  type SetFlightSearchContext,
  type SetFlightShopContext,
  ParentState,
  type CartQuoteContext,
} from "@hopper-b2b/checkout";
import {
  type ITripTerminus,
  TripCategory,
  type TripDetails,
} from "@hopper-b2b/types";

import { emailRegex } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import type { History } from "history";
import queryStringParser from "query-string";
import { UserContext } from "../../../App";
import { NubankPaymentEvent } from "../../../checkout/";
import { LoadingScreen } from "../../../checkout/components/LoadingScreen";

import { decodeEmailAndPhone } from "../../../checkout/helpers";
import {
  EnrollmentResponse,
  NUPAY_AUTH_CODE_PARAM,
  NUPAY_ERROR_DESCRIPTION_PARAM,
  NUPAY_ERROR_PARAM,
  NUPAY_STATE_PARAM,
  PAYMENT_ENROLLMENT_STATUS_PARAM,
  parseNupayState,
  nupayStateToSearchParams,
  consumeCheckoutContextFromStorage,
  isMinimalNupayState,
} from "../../../checkout/states/payments/api/checkAuthStatus";
import { nubankPhoneRegex } from "../../../components/Slots/ContactInfoForm/component";
import {
  PATH_FLIGHTS_SEARCH,
  PATH_FLIGHTS_SHOP,
} from "../../../utils/urlPaths";
import { Event } from "../events";
import { validateContext } from "../utilities";

/**
 * On NuPay OAuth callback the URL is just ?state=<base64>&code=...
 * Decode the state and replace the URL synchronously (during initial
 * state) so that ResumeCheckoutComponent sees the decoded params on
 * its first render.
 */
const useRestoreNupayState = (): boolean => {
  const history = useHistory();

  const [restored] = useState(() => {
    const params = queryStringParser.parse(history.location.search);
    const stateParam = params[NUPAY_STATE_PARAM] as string;
    if (!stateParam) return true; // nothing to restore

    // Try parsing state from the OAuth callback. If the BFF used minimal state
    // (UUID-only), the parsed result won't have checkout fields — fall back to
    // localStorage where the FE saved the context before the redirect.
    let nupayState = parseNupayState(stateParam);
    const isMinimal = !nupayState || isMinimalNupayState(nupayState);
    if (isMinimal) {
      const stored = consumeCheckoutContextFromStorage();
      if (stored) {
        nupayState = stored;
      } else if (!nupayState) {
        return true;
      }
    }

    const restoredSearch = nupayStateToSearchParams(nupayState);

    const authCode = params[NUPAY_AUTH_CODE_PARAM] as string;
    const error = params[NUPAY_ERROR_PARAM] as string;
    const errorDesc = params[NUPAY_ERROR_DESCRIPTION_PARAM] as string;

    const restoredParams = new URLSearchParams(restoredSearch);
    if (authCode) {
      restoredParams.set(
        PAYMENT_ENROLLMENT_STATUS_PARAM,
        EnrollmentResponse.success
      );
      restoredParams.set(NUPAY_AUTH_CODE_PARAM, authCode);
    } else if (error) {
      restoredParams.set(
        PAYMENT_ENROLLMENT_STATUS_PARAM,
        EnrollmentResponse.failure
      );
      restoredParams.set(NUPAY_ERROR_PARAM, error);
      if (errorDesc)
        restoredParams.set(NUPAY_ERROR_DESCRIPTION_PARAM, errorDesc);
    }
    restoredParams.set(NUPAY_STATE_PARAM, stateParam);

    history.replace({
      pathname: history.location.pathname,
      search: restoredParams.toString(),
    });

    return true;
  });

  return restored;
};

export const RefreshCheckout = () => {
  const { sessionInfo } = useContext(UserContext);

  // Restore checkout URL params from NuPay OAuth state synchronously
  // before ResumeCheckoutComponent mounts and runs its steps.
  useRestoreNupayState();

  return sessionInfo?.userInfoResponse?.userId ? (
    <ResumeCheckoutComponent
      steps={getNubankRefreshSteps(sessionInfo.userInfoResponse.userId)}
      validateContext={validateContext}
    >
      <LoadingScreen />
    </ResumeCheckoutComponent>
  ) : null;
};

const contactInfoValidator = (userId: string) => async (history: History) => {
  try {
    const { email, phone } = parseFlightsQueryString(history);
    const { decodedEmail, decodedPhone } = await decodeEmailAndPhone({
      email,
      phone,
      userId,
    });
    if (
      decodedEmail &&
      decodedPhone &&
      emailRegex.test(decodedEmail) &&
      nubankPhoneRegex.test(decodedPhone)
    ) {
      return true;
    }
    return false;
  } catch (error) {
    console.log("decoding failed in contactInfoValidator", error);
    return false;
  }
};

const getNubankRefreshSteps = (userId: string): Array<ResumeStep> => [
  // Step 1: check if we have flight search params, if not kick them to PATH_FLIGHTS_SEARCH
  {
    fallback: { url: `/${PATH_FLIGHTS_SEARCH}` },
    validator: (history) => {
      const { tripCategory, origin, destination, departureDate, returnDate } =
        parseFlightsQueryString(history);

      const effectiveCategory = resolveEffectiveTripCategory(
        tripCategory,
        returnDate
      );

      if (
        [TripCategory.ONE_WAY, TripCategory.ROUND_TRIP].includes(
          effectiveCategory
        )
      ) {
        if (IATA_REGEX.test(origin) && IATA_REGEX.test(destination)) {
          if (departureDate && dayjs(departureDate).isValid()) {
            if (effectiveCategory === TripCategory.ONE_WAY) {
              return true;
            } else if (
              effectiveCategory === TripCategory.ROUND_TRIP &&
              returnDate &&
              dayjs(returnDate).isValid()
            ) {
              return true;
            }
          }
        }
      }
      return false;
    },
    getStepEvent: (history) => {
      const { tripCategory, departureDate, returnDate } =
        parseFlightsQueryString(history);
      const effectiveCategory = resolveEffectiveTripCategory(
        tripCategory,
        returnDate
      );
      return {
        type: ResumeEvent.SET_FLIGHT_SEARCH_CONTEXT,
        tripCategory: effectiveCategory,
        departureDate,
        returnDate,
      } as SetFlightSearchContext;
    },
    requests: [
      {
        api: fetchLocationAutocomplete,
        requestPayload: (history) => {
          const { origin } = parseFlightsQueryString(history);
          return { LocationQuery: "Label", l: origin };
        },
        responseEvent: (response: LocationResponse, history) => {
          const { origin } = parseFlightsQueryString(history);
          return {
            type: ResumeEvent.SET_FLIGHT_SEARCH_ORIGIN,
            origin: getFlightSelectedLocation(
              response,
              origin
            ) as ITripTerminus,
          };
        },
      },
      {
        api: fetchLocationAutocomplete,
        requestPayload: (history) => {
          const { destination } = parseFlightsQueryString(history);
          return { LocationQuery: "Label", l: destination };
        },
        responseEvent: (response: LocationResponse, history) => {
          const { destination } = parseFlightsQueryString(history);
          return {
            type: ResumeEvent.SET_FLIGHT_SEARCH_DESTINATION,
            destination: getFlightSelectedLocation(
              response,
              destination
            ) as ITripTerminus,
          };
        },
      },
    ],
  },
  // Step 2: check if flight shop params, if not kick them to PATH_FLIGHTS_SHOP
  {
    fallback: { url: `/${PATH_FLIGHTS_SHOP}` },
    validator: (history) => {
      const { tripId } = parseFlightsQueryString(history);
      return !!tripId;
    },
    requests: [
      {
        api: fetchTripDetails,
        requestPayload: (history) => {
          const { tripId } = parseFlightsQueryString(history);
          return tripId;
        },
        responseEvent: (response: TripDetails, history) => {
          const { outgoingFareId, returnFareId } =
            parseFlightsQueryString(history);
          return {
            type: ResumeEvent.SET_FLIGHT_TRIP_DETAILS,
            tripDetails: response,
            selectedFareId: returnFareId || outgoingFareId,
          };
        },
      },
    ],
    getStepEvent: (history) => {
      const { tripId, returnFareId, outgoingFareId } =
        parseFlightsQueryString(history);
      return {
        type: ResumeEvent.SET_FLIGHT_SHOP_CONTEXT,
        tripId,
        returnFareId,
        outgoingFareId,
      } as SetFlightShopContext;
    },
  },
  // Step 2b: Fintech products step
  // Note: Price drop candidateId is set by FlightsCheckout via contextToInitialize.
  // We no longer override it here to avoid overwriting with undefined when the URL
  // doesn't have priceDropCandidateId (normal flow vs resume flow).
  {
    fallback: { url: `/${PATH_FLIGHTS_SHOP}` },
    validator: () => {
      // We want always try adding fintech products, defaulting to true
      return true;
    },
    // getStepEvent intentionally omitted - candidateId comes from contextToInitialize
  },
  // Step 3: Contact Information
  {
    fallback: { event: ResumeEvent.GO_TO_CONTACT_INFORMATION },
    validator: contactInfoValidator(userId),
    getStepEvent: async (history) => {
      try {
        const { email, phone } = parseFlightsQueryString(history);
        const { decodedEmail, decodedPhone } = await decodeEmailAndPhone({
          email,
          phone,
          userId,
        });
        return {
          type: ResumeEvent.SET_CONTACT_INFORMATION_CONTEXT,
          email: decodedEmail,
          phone: decodedPhone,
        } as SetContactInformationContext;
      } catch (error) {
        console.log("decoding failed in getStepEvent", error);
        return { type: ResumeEvent.GO_TO_CONTACT_INFORMATION };
      }
    },
  },
  // Step 4: Passenger Information
  {
    fallback: { event: ResumeEvent.GO_TO_PASSENGER_SELECT },
    validator: (history) => {
      const { selectedPassengerIds } = parseFlightsQueryString(history);
      if (selectedPassengerIds?.length && selectedPassengerIds?.length > 0) {
        return true;
      }
      return false;
    },
    requests: [
      {
        api: fetchUserPassengers,
        responseEvent: (response: PersonListResponse, history) => {
          const { selectedLapInfantIds, selectedPassengerIds } =
            parseFlightsQueryString(history);

          if (response.Response === "Success") {
            const passengers = (response as PersonListSuccess).value;
            const passengerIds = passengers.map((p) => p.id);
            const validLapInfants =
              selectedLapInfantIds?.length > 0
                ? selectedLapInfantIds.every((id) => passengerIds.includes(id))
                : true;
            if (
              selectedPassengerIds.every((id) => passengerIds.includes(id)) &&
              validLapInfants
            ) {
              return {
                type: ResumeEvent.SET_PASSENGER_INFORMATION_CONTEXT,
                userPassengers: passengers,
                selectedPassengerIds,
                selectedLapInfantIds,
              };
            }
          }
          return {
            type: Event.GO_TO_PASSENGER_SELECT,
          };
        },
      },
    ],
  },
  // Step 4b: Passenger Validation & Trip Pricing
  {
    fallback: { event: ResumeEvent.GO_TO_PASSENGER_SELECT },
    validator: (history) => {
      const { selectedPassengerIds } = parseFlightsQueryString(history);
      if (selectedPassengerIds?.length && selectedPassengerIds?.length > 0) {
        return true;
      }
      return false;
    },
    requests: [
      {
        api: validateUserPassengers,
        requestPayload: (history) => {
          const {
            tripId,
            outgoingFareId,
            returnFareId,
            selectedPassengerIds,
            selectedLapInfantIds,
          } = parseFlightsQueryString(history);

          const payload: PassengersForFareRequest = {
            seatedPassengers: selectedPassengerIds,
            tripId: tripId,
            fareId: returnFareId ? returnFareId : outgoingFareId,
            lapInfants: selectedLapInfantIds,
            ancillaryIds: [],
          };
          return payload;
        },
        responseEvent: (response: PassengersResponse) => {
          if (
            response.PassengersResponse === PassengersResponseEnum.PassengersOk
          ) {
            return {
              type: ResumeEvent.SET_PASSENGER_VALIDATION,
            };
          } else {
            // Throw an error to run the error flow and run the fallback
            throw Error("invalid passengers");
          }
        },
      },
      {
        api: fetchTripPricing,
        requestPayload: (history) => {
          const {
            tripId,
            outgoingFareId,
            returnFareId,
            selectedPassengerIds,
            selectedLapInfantIds,
          } = parseFlightsQueryString(history);

          const payload: PassengersForFareRequest = {
            seatedPassengers: selectedPassengerIds,
            tripId: tripId,
            fareId: returnFareId ? returnFareId : outgoingFareId,
            lapInfants: selectedLapInfantIds,
            ancillaryIds: [],
          };
          return payload;
        },
        responseEvent: (response: TripPricingSummaryResponse) => {
          if ((response as Failure).errors) {
            // Throw an error to run the error flow and run the fallback
            throw Error((response as Failure).errors.join(". "));
          }

          return {
            type: ResumeEvent.SET_TRIP_PRICING,
            tripSummary: (response as TripPricingSummarySuccess).value,
          };
        },
      },
    ],
  },
  // Step 5: Cart Quote
  {
    fallback: { event: ResumeEvent.GO_TO_PASSENGER_SELECT },
    validator: (history) => {
      const { cartToken } = parseFlightsQueryString(history);
      return !!cartToken;
    },
    requests: [
      {
        api: getQuoteBreakdown,
        requestPayload: (history) => {
          const { cartToken } = parseFlightsQueryString(history);
          return { value: cartToken } as CipherText;
        },
        responseEvent: (response: QuoteBreakdownResponse, history) => {
          const { cartToken } = parseFlightsQueryString(history);

          if (
            response.quoteBreakdown.state === QuoteState.Empty ||
            response.quoteBreakdown.state === QuoteState.Unavailable ||
            response.quoteBreakdown.state === QuoteState.Fulfilled
          ) {
            throw Error(
              `quoteBreakdown error with quoteState: ${response.quoteBreakdown.state}`
            );
          }

          return {
            type: ResumeEvent.SET_CART_QUOTE_CONTEXT,
            breakdown: response,
            cipherText: { value: cartToken },
          };
        },
        errorEvent: () => ({ type: ResumeEvent.GO_TO_PASSENGER_SELECT }),
      },
    ],
    getStepEvent: () => ({
      type: ResumeEvent.GO_TO_REVIEW,
    }),
  },
  // Step 5b: Add Seat Availability
  {
    fallback: { event: ResumeEvent.GO_TO_PASSENGER_SELECT },
    validator: (history) => {
      const { cartToken } = parseFlightsQueryString(history);
      return !!cartToken;
    },
    skipStep: (context, _history) => {
      // Check quote context Flight product
      const flightProduct = (
        context[ParentState.cartQuote] as CartQuoteContext
      )?.quoteBreakdown?.products?.find(
        (product) =>
          product.product.type === Product.Flight ||
          product.product.type === Product.MultiProviderFlight
      );
      if (
        flightProduct &&
        flightProduct.product.value?.seatMap?.SeatMapResponse ===
          SeatMapResponseEnum.SeatMapAvailable
      ) {
        return false;
      }
      return true;
    },
    getStepEvent: () => ({
      type: ResumeEvent.SET_SEATS_CONTEXT,
      availability: SeatMapResponseEnum.SeatMapAvailable,
    }),
  },
  // Step 6: Payment Enrollment
  {
    fallback: { event: ResumeEvent.GO_TO_REVIEW },
    validator: (history) => {
      const { enrollmentResponse } = parsePaymentQueryParams(history);
      return (
        enrollmentResponse === EnrollmentResponse.failure ||
        enrollmentResponse === EnrollmentResponse.success
      );
    },
    getStepEvent: (history) => {
      const { enrollmentResponse, authorizationCode, nupayOAuthState } =
        parsePaymentQueryParams(history);
      if (enrollmentResponse === EnrollmentResponse.success) {
        return {
          type: NubankPaymentEvent.GO_TO_PAYMENT_ENROLLMENT_SUCCESS,
          authorizationCode,
          nupayOAuthState,
        };
      } else {
        return {
          type: NubankPaymentEvent.GO_TO_PAYMENT_ENROLLMENT_FAILURE,
          nupayOAuthState,
        };
      }
    },
  },
];

const IATA_REGEX = /^[a-z]{3}$/i;

/**
 * Resolves the effective TripCategory for the RestartCheckout resume flow.
 *
 * When tripCategory is absent from the restored post-OAuth URL (because the
 * hydration race, fixed in PR #18256, transiently dropped it from the URL
 * before buildNupayState() captured it), infer the category from returnDate:
 * ROUND_TRIP when a return date is present, ONE_WAY otherwise.
 *
 * This is a defence-in-depth companion to the same inference in
 * buildNupayState(): even if the stored NupayState has tripCategory, URL
 * reconstruction could theoretically strip it in future. Either layer alone is
 * sufficient; both together make the resume flow resilient.
 *
 * @internal Exported for unit testing.
 */
export const resolveEffectiveTripCategory = (
  tripCategory: TripCategory | undefined,
  returnDate: string | Date | null | undefined
): TripCategory =>
  tripCategory ?? (returnDate ? TripCategory.ROUND_TRIP : TripCategory.ONE_WAY);

export const parsePaymentQueryParams = (history: History) => {
  const queryString = history?.location?.search || "";
  const parsedQueryStringPrimitive = queryStringParser.parse(queryString);

  // Check for Nupay direct OAuth callback params first
  const nupayAuthCode = parsedQueryStringPrimitive[
    NUPAY_AUTH_CODE_PARAM
  ] as string;
  const nupayError = parsedQueryStringPrimitive[NUPAY_ERROR_PARAM] as string;
  const nupayStateParam = parsedQueryStringPrimitive[
    NUPAY_STATE_PARAM
  ] as string;

  if (nupayAuthCode && typeof nupayAuthCode === "string") {
    return {
      enrollmentResponse: EnrollmentResponse.success,
      authorizationCode: nupayAuthCode.trim(),
      nupayOAuthState: nupayStateParam,
    };
  }
  if (nupayError) {
    const nupayErrorDescription = parsedQueryStringPrimitive[
      NUPAY_ERROR_DESCRIPTION_PARAM
    ] as string;
    console.warn(
      `[NupayDirect] Enrollment failed: error=${nupayError}, description=${nupayErrorDescription}`
    );
    return {
      enrollmentResponse: EnrollmentResponse.failure,
      authorizationCode: undefined,
      nupayOAuthState: nupayStateParam,
    };
  }

  // Fall back to legacy enrollment param (Ebanx flow)
  return {
    enrollmentResponse: parsedQueryStringPrimitive[
      PAYMENT_ENROLLMENT_STATUS_PARAM
    ] as string,
    authorizationCode: undefined,
    nupayOAuthState: undefined,
  };
};
